import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertCircle, CheckCircle2, Mail } from 'lucide-react'
import { SiteLayout } from '@/components/site/SiteLayout'
import { AuthCard } from '@/components/auth/AuthCard'
import { useAuth } from '@/lib/auth'

/**
 * "Check your email" landing page after any successful signup.
 *
 * Email is read from sessionStorage (set by SignUpPage at navigation
 * time) instead of a URL query param so it stays out of browser
 * history, server logs, screenshots, and Referer headers. Survives
 * manual refresh in the same tab and clears when the tab closes —
 * the right lifecycle for an ephemeral signup handoff. If the user
 * arrives here directly (no sessionStorage entry), the copy
 * gracefully degrades to a generic "we sent a link".
 *
 * Supabase's anti-enumeration blanks the signup response so we can't
 * tell client-side whether the email was new or a duplicate. This page
 * is the universal destination: it presents the same "check your inbox"
 * UX with a Resend button (for cases where the email got lost) and an
 * "Already verified? Sign in" link (for duplicate-confirmed cases).
 */
export function VerifyEmailPage() {
  const { resendVerification } = useAuth()
  const [email] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem('signup-pending-email')
    } catch {
      return null
    }
  })

  const [resendBusy, setResendBusy] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [resendOk, setResendOk] = useState(false)

  async function onResend() {
    if (!email) {
      setResendError('No email on file — please return to sign up.')
      return
    }
    setResendBusy(true)
    setResendError(null)
    setResendOk(false)
    const { error } = await resendVerification(email)
    setResendBusy(false)
    if (error) {
      setResendError(error.message)
      return
    }
    setResendOk(true)
  }

  return (
    <SiteLayout>
      <AuthCard
        title="Check Your Email"
        subtitle={
          <>
            We sent a verification link
            {email ? (
              <>
                {' '}
                to <span className="text-[var(--color-text)] font-medium">{email}</span>
              </>
            ) : null}
            . Click the link to confirm your account before signing in.
          </>
        }
        footer={
          <>
            Wrong address?{' '}
            <Link
              to="/sign-up"
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Sign up again
            </Link>
            .
          </>
        }
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <div
            aria-hidden
            className={[
              'inline-flex items-center justify-center w-14 h-14 rounded-full',
              'bg-[var(--color-accent-soft)] text-[var(--color-accent)]',
            ].join(' ')}
          >
            <Mail size={26} strokeWidth={2} />
          </div>
          <p className="text-[13px] text-[var(--color-muted)] text-center">
            The link expires in 24 hours. If you don’t see the email, check your
            spam folder.
          </p>

          <button
            type="button"
            onClick={onResend}
            disabled={resendBusy || !email}
            className={[
              'inline-flex items-center justify-center gap-2',
              'mt-1 px-3.5 py-2 rounded-[var(--radius-DEFAULT)]',
              'border border-[var(--color-accent)]/40 bg-[var(--color-accent-soft)]',
              'text-[13px] text-[var(--color-accent)] font-medium',
              'hover:bg-[var(--color-accent-soft)]/80 transition-colors cursor-pointer',
              'disabled:opacity-60 disabled:cursor-not-allowed',
            ].join(' ')}
          >
            <Mail size={14} aria-hidden />
            {resendBusy ? 'Sending…' : 'Resend verification email'}
          </button>

          {resendOk ? (
            <p
              role="status"
              className={[
                'flex items-center gap-2 text-[13px]',
                'text-[var(--color-accent)]',
              ].join(' ')}
            >
              <CheckCircle2 size={14} aria-hidden />
              Sent — check your inbox.
            </p>
          ) : null}
          {resendError ? (
            <p
              role="alert"
              className={[
                'flex items-center gap-2 text-[13px]',
                'text-[var(--color-signal)]',
              ].join(' ')}
            >
              <AlertCircle size={14} aria-hidden />
              {resendError}
            </p>
          ) : null}

          <p className="text-[12.5px] text-[var(--color-muted)] text-center mt-1">
            Already verified?{' '}
            <Link
              to="/sign-in"
              className="text-[var(--color-accent)] underline underline-offset-2"
            >
              Sign in
            </Link>
            .
          </p>
        </div>
      </AuthCard>
    </SiteLayout>
  )
}
